// Persistent business-identity footer — required by Twilio A2P 10DLC
// reviewer notes (rejection code 30919). Must appear on EVERY public
// page so the brand identity, contact details, and compliance link
// hub are reachable without authentication.

import React from "react";
import { Link } from "react-router-dom";
import { BUSINESS_IDENTITY } from "../lib/business_identity";
import { Mail, Phone, MapPin } from "lucide-react";

export default function PublicFooter() {
  const b = BUSINESS_IDENTITY;
  return (
    <footer
      className="mt-16 border-t border-emerald-900/30 bg-emerald-950 text-emerald-50/90"
      data-testid="public-footer"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10 grid grid-cols-1 md:grid-cols-3 gap-8 text-sm">
        {/* Column 1 — Identity */}
        <div>
          <div className="font-semibold text-emerald-50 text-base" data-testid="footer-legal-name">
            {b.legalName}
          </div>
          <div className="text-emerald-50/70 text-xs mt-0.5">
            d/b/a <span data-testid="footer-trade-name">{b.tradeName}</span>
          </div>
          <div className="mt-4 space-y-1.5 text-emerald-50/85 text-xs">
            <div className="flex items-start gap-2" data-testid="footer-address">
              <MapPin className="w-3.5 h-3.5 mt-0.5 shrink-0 text-amber-300" />
              <div>
                {b.addressLines.map((l, i) => (
                  <div key={i}>{l}</div>
                ))}
              </div>
            </div>
            <a
              href={`mailto:${b.supportEmail}`}
              className="flex items-center gap-2 hover:text-amber-300 transition"
              data-testid="footer-email"
            >
              <Mail className="w-3.5 h-3.5 text-amber-300" />
              {b.supportEmail}
            </a>
            <a
              href={`tel:${b.phoneE164}`}
              className="flex items-center gap-2 hover:text-amber-300 transition"
              data-testid="footer-phone"
            >
              <Phone className="w-3.5 h-3.5 text-amber-300" />
              {b.phoneDisplay}
            </a>
          </div>
        </div>

        {/* Column 2 — Compliance links */}
        <div>
          <div className="font-semibold text-emerald-50 text-xs uppercase tracking-wider mb-3">
            Legal &amp; Compliance
          </div>
          <ul className="space-y-1.5 text-emerald-50/85 text-xs">
            <li>
              <Link to="/privacy-policy" className="hover:text-amber-300 transition" data-testid="footer-link-privacy">
                Privacy Policy
              </Link>
            </li>
            <li>
              <Link to="/terms-of-service" className="hover:text-amber-300 transition" data-testid="footer-link-terms">
                Terms of Service
              </Link>
            </li>
            <li>
              <Link to="/sms-program" className="hover:text-amber-300 transition" data-testid="footer-link-sms-program">
                SMS / Text Message Program
              </Link>
            </li>
            <li>
              <Link to="/contact" className="hover:text-amber-300 transition" data-testid="footer-link-contact">
                Contact Us
              </Link>
            </li>
          </ul>
        </div>

        {/* Column 3 — Description */}
        <div>
          <div className="font-semibold text-emerald-50 text-xs uppercase tracking-wider mb-3">
            About
          </div>
          <p className="text-xs leading-relaxed text-emerald-50/80" data-testid="footer-about">
            {b.tradeName} is an AI-powered customer engagement platform that lets businesses
            capture leads, track customer lifecycle, manage retail orders, and engage
            customers via secure messaging — purpose-built for small &amp; mid-market
            retail, restaurant, healthcare, and professional services businesses.
          </p>
        </div>
      </div>
      <div className="border-t border-emerald-50/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex flex-col sm:flex-row items-center justify-between gap-2 text-[11px] text-emerald-50/60">
          <div>© {new Date().getFullYear()} {b.legalName}. All rights reserved.</div>
          <div>
            Need help? Email <a href={`mailto:${b.supportEmail}`} className="underline hover:text-amber-300">{b.supportEmail}</a>
          </div>
        </div>
      </div>
    </footer>
  );
}
